import { IUser } from 'core/api/users/users-api-interface';
import UsersApiService from 'core/api/users/users-api.service';
import { getRoleNameFromKey, Role } from 'core/config/roles';
import { useDialog } from 'core/providers/DialogProvider';
import { PageElement } from 'core/utilities/interface-helpers';
import { QuerySnapshot } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import SharedButton from 'shared/components/buttons/button';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import {
  ISharedTableColumn,
  ISharedTableCustomCellTemplate,
  ISharedTableRow,
} from 'shared/components/table/table-interface';
import DeleteUserDialog from '../delete-user-dialog/delete-user-dialog';
import { Unsubscribe } from 'firebase/firestore';
import { useFlags } from '@atlaskit/flag';
import { showErrorFlag } from 'core/utilities/flags-helper';
import AddUserDialog from '../add-user/add-user-dialog/add-user-dialog';
import DriverRoutesDialog from '../driver-routes-dialog/driver-routes-dialog';
import UsersListResetPasswordButton from './users-list-reset-password-button';

const UsersTableColumns: ISharedTableColumn[] = [
  { label: 'User', key: 'user', templateId: 'user', width: 45 },
  { label: 'Role', key: 'roleLabel', templateId: 'text', width: 45 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

interface IUsersListRow extends IUser {
  roleLabel: string;
}

const UsersFilters: Filter[] = [{ id: 'name', label: 'Name' }];

const UsersList = () => {
  const [loading, setLoading] = useState(false);
  const [tableRows, setTableRows] = useState<ISharedTableRow<IUsersListRow>[]>([]);
  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);

  const dialog = useDialog();
  const flags = useFlags();

  useEffect(() => {
    const handleSnapshot = (querySnapshot: QuerySnapshot<IUser>) => {
      const userRows: ISharedTableRow<IUsersListRow>[] = [];
      querySnapshot.forEach((userSnapShot) => {
        const userData = userSnapShot.data();
        let roleLabel: string;
        if (userData.roles) {
          roleLabel = userData.roles.map((role) => getRoleNameFromKey(role)).join(', ');
        } else {
          roleLabel = getRoleNameFromKey(userData.role!);
        }
        const rowData = {
          key: userData.uid,
          data: {
            ...userData,
            roleLabel,
          },
        };
        userRows.push(rowData);
      });
      userRows.sort((a, b) => a.data.fullName.localeCompare(b.data.fullName));
      setTableRows(userRows);
      setLoading(false);
    };

    const handleSubscriptionError = (error: any) => {
      showErrorFlag('An error occurred', 'Could not retrieve users, please try again.', flags);
    };

    let unsubscribe: Unsubscribe;
    setLoading(true);
    unsubscribe = UsersApiService.subscribeToAllUsers(handleSnapshot, handleSubscriptionError);

    return () => {
      unsubscribe();
    };
  }, [flags]);

  const filteredRows = useMemo(() => {
    return tableRows.filter((row) => {
      return filterQuery.every((filter) => {
        if (!filter.value) return true;
        switch (filter.id) {
          case 'name':
            return row.data.fullName.toLowerCase().includes((filter.value as string).toLowerCase());
          default:
            return true;
        }
      });
    });
  }, [tableRows, filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery]
  );

  const userCellTemplate = (user: IUser) => {
    return (
      <div className='flex'>
        <div className='h-10 w-10 rounded-full bg-primary mr-3 flex items-center justify-center text-white body-01'>
          {user.fullName.charAt(0).toUpperCase()}
        </div>
        <div className='flex flex-col'>
          <p>{user.fullName}</p>
          <p className='text-gray-400'>{user.emailAddress}</p>
        </div>
      </div>
    );
  };
  const userHasRole = (user: IUser, role: Role) => user.roles?.includes(role) || user.role === role;

  const actionCellTemplate = (user: IUser) => (
    <div className='w-full flex justify-end space-x-2'>
      {userHasRole(user, Role.PHARMACY_DRIVER) && (
        <SharedButton
          onClick={() => dialog?.openDialog(<DriverRoutesDialog user={user} />)}
          type='button'
          appearance='primary'
          label='Select routes'
        />
      )}
      {!userHasRole(user, Role.SUPER_ADMIN) && <UsersListResetPasswordButton user={user} />}
      <SharedButton
        onClick={() => dialog?.openDialog(<AddUserDialog user={user} />)}
        type='button'
        appearance='primary'
        label='Edit'
      />
      <SharedButton
        onClick={() => dialog?.openDialog(<DeleteUserDialog user={user} />)}
        type='button'
        appearance='danger'
        label='Delete'
      />
    </div>
  );

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: userCellTemplate,
      id: 'user',
    },
    {
      template: actionCellTemplate,
      id: 'action',
    },
  ];
  const headerActions: PageElement[] = [
    {
      key: 'addUser',
      element: (
        <SharedButton
          onClick={() => dialog?.openDialog(<AddUserDialog />)}
          type='button'
          appearance='primary'
          label='Add User'
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader title='Users' actions={headerActions} />
      <div className='mt-4'>
        <SharedFilters filters={UsersFilters} onFilterValueChange={handleFilterChange} />
        <SharedTable
          columns={UsersTableColumns}
          customTemplates={customTemplates}
          rows={filteredRows}
          isLoading={loading}
        />
      </div>
    </>
  );
};

export default UsersList;
